import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaArrowLeft, FaMoneyBillWave, FaIdCard, FaInfoCircle, FaUser, FaUniversity, FaPhone, FaMapMarkerAlt, FaExternalLinkAlt } from 'react-icons/fa';
import axios from '../api/axios';

export default function BancoDetail() {
  const { codigo } = useParams();
  const [banco, setBanco] = useState(null);
  const [tercero, setTercero] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBancoAndTercero = async () => {
      setIsLoading(true);
      try {
        const bancoResponse = await axios.get(`/bancos/${codigo}`);
        setBanco(bancoResponse.data);
        
        const terceroResponse = await axios.get(`/terceros/${bancoResponse.data.documentoTercero}`);
        setTercero(terceroResponse.data);
      } catch (err) {
        setError('Error al cargar los detalles del banco o tercero. Por favor, intente de nuevo.');
        console.error('Error fetching banco or tercero details:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBancoAndTercero();
  }, [codigo]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded" role="alert">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!banco || !tercero) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded" role="alert">
          <p className="font-bold">Advertencia</p>
          <p>No se encontró el banco o el tercero asociado</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Link to="/home/bancos" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-6 transition-colors duration-200">
        <FaArrowLeft className="mr-2" /> Volver a la lista de bancos
      </Link>
      <div className="bg-white shadow-2xl rounded-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-6 text-white">
          <h2 className="text-3xl font-bold flex items-center">
            <FaUniversity className="mr-4 text-4xl" />
            {banco.nombreBanco}
          </h2>
          <p className="mt-2 text-blue-100 flex items-center">
            <FaIdCard className="mr-2" /> Código: {banco.codigo}
          </p>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-6 rounded-lg shadow-md transition-all duration-300 hover:shadow-lg">
              <h3 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
                <FaMoneyBillWave className="mr-2 text-green-600" /> Información Bancaria
              </h3>
              <p className="text-gray-700"><strong className="text-gray-900">IBAN:</strong> {banco.iban}</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg shadow-md transition-all duration-300 hover:shadow-lg">
              <h3 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
                <FaIdCard className="mr-2 text-blue-600" /> Información del Tercero
              </h3>
              <p className="text-gray-700"><strong className="text-gray-900">Documento:</strong> {banco.documentoTercero}</p>
              <p className="text-gray-700"><strong className="text-gray-900">Nombre:</strong> {tercero.nombre}</p>
            </div>
          </div>
          <div className="bg-gray-50 p-6 rounded-lg shadow-md transition-all duration-300 hover:shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
              <FaInfoCircle className="mr-2 text-yellow-600" /> Descripción
            </h3>
            <p className="text-gray-700">{banco.descripcion || 'No hay descripción disponible'}</p>
          </div>
          <div className="bg-gray-50 p-6 rounded-lg shadow-md transition-all duration-300 hover:shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
              <FaUser className="mr-2 text-purple-600" /> Detalles Adicionales del Tercero
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <p className="text-gray-700 flex items-center">
                <FaPhone className="mr-2 text-gray-500" />
                <strong className="text-gray-900 mr-2">Teléfono:</strong> {tercero.telefono || 'No disponible'}
              </p>
              <p className="text-gray-700 flex items-center">
                <FaMapMarkerAlt className="mr-2 text-gray-500" />
                <strong className="text-gray-900 mr-2">Dirección:</strong> {tercero.domicilio || 'No disponible'}
              </p>
            </div>
            <Link 
              to={`/home/terceros/${banco.documentoTercero}`} 
              className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors duration-200"
            >
              Ver detalles completos del tercero
              <FaExternalLinkAlt className="ml-2" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}